import type { PlanDto, PlanSegmentDto } from '@room-mates/shared';
import { type ReactElement, useState } from 'react';
import { formatDate } from '../../shared/format.js';
import { bedKindLabel } from '../../shared/i18n/dictionary.js';
import { useTranslation } from '../../shared/i18n/useLanguage.js';
import { Alert, Badge, Button, Card } from '../../shared/ui/index.js';
import { PlanAlternatives, PlanReasons } from './PlanReasons.js';

export interface PlanSegmentViewProps {
  readonly plan: PlanDto;
  readonly segment: PlanSegmentDto;
}

/**
 * The arrangement during one stretch of unchanging nights, grouped the way an
 * organiser walks the building: house, then room, then who is in it.
 */
export function PlanSegmentView({ plan, segment }: PlanSegmentViewProps): ReactElement {
  const t = useTranslation();
  const assignments = plan.assignments.filter(
    (assignment) => assignment.segmentIndex === segment.index,
  );
  const unassigned = plan.unassigned.filter((entry) => entry.segmentIndex === segment.index);

  const rooms = new Map<
    string,
    { houseName: string; roomName: string; entries: typeof assignments }
  >();
  for (const assignment of assignments) {
    const existing = rooms.get(assignment.roomId);
    if (existing === undefined) {
      rooms.set(assignment.roomId, {
        houseName: assignment.houseName,
        roomName: assignment.roomName,
        entries: [assignment],
      });
      continue;
    }
    existing.entries.push(assignment);
  }

  const grouped = [...rooms.entries()].sort(([, a], [, b]) =>
    `${a.houseName}${a.roomName}`.localeCompare(`${b.houseName}${b.roomName}`),
  );

  return (
    <div className="stack">
      <p className="card__meta">
        {formatDate(segment.start)} → {formatDate(segment.end)} · {t.count.nights(segment.nights)} ·{' '}
        {t.planPage.onSite(segment.guestIds.length)}
      </p>

      {unassigned.length > 0 ? (
        <Alert tone="error">
          <div className="stack stack--tight">
            <strong>{t.planPage.nobodyCouldBeFoundABedFor}</strong>
            {unassigned.map((entry) => (
              <span key={entry.guestId}>
                {entry.guestName} — {entry.message}
              </span>
            ))}
          </div>
        </Alert>
      ) : null}

      {grouped.map(([roomId, room]) => (
        <Card key={roomId}>
          <div className="stack">
            <div className="row row--between">
              <h3 className="card__title">
                {room.roomName}
                <span className="card__meta">{room.houseName}</span>
              </h3>
              <Badge>{t.planPage.inThisRoom(room.entries.length)}</Badge>
            </div>

            <div className="stack stack--tight">
              {room.entries.map((assignment) => (
                <AssignmentRow key={assignment.guestId} assignment={assignment} />
              ))}
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}

function AssignmentRow({
  assignment,
}: {
  assignment: PlanDto['assignments'][number];
}): ReactElement {
  const t = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <div className="list-row" style={{ alignItems: 'flex-start', flexDirection: 'column' }}>
      <div className="row row--between" style={{ width: '100%' }}>
        <div className="list-row__main">
          <span className="list-row__title">
            {assignment.guestName}
            {assignment.lockedByUser ? (
              <>
                {' '}
                <Badge tone="accent">{t.planPage.lockedByYou}</Badge>
              </>
            ) : null}
          </span>
          <span className="list-row__meta" title={assignment.bedName}>
            {bedKindLabel(t, assignment.bedType, assignment.bedOption)}
          </span>
          <span className="list-row__meta">{assignment.headline}</span>
        </div>
        <Button small variant="ghost" onClick={() => setOpen(!open)} aria-expanded={open}>
          {open ? t.planPage.hideDetails : t.planPage.why}
        </Button>
      </div>

      {open ? (
        <div className="stack stack--tight" style={{ width: '100%' }}>
          <PlanReasons reasons={assignment.reasons} />
          <PlanAlternatives assignment={assignment} />
        </div>
      ) : null}
    </div>
  );
}
